import { format } from 'date-fns';
import { utcCalendarDateToLocal } from './dateFormatHelpers';

/**
 * Formats a timeframe from "Month Year" format to display format
 * Example: "January 2025" → "January 2025"
 * Also handles weekly date range format: "Nov. 18-24, 2024" or "Oct. 28 - Nov. 3, 2024"
 * For backward compatibility, also handles "Week of [Month] [Day], [Year]" format
 */
export const formatTimeframeFromWeekOf = (timeframe: string): string => {
  // Check if it's already in monthly format (e.g., "January 2025")
  const monthlyMatch = RegExp(/^([A-Za-z]+) (\d{4})$/).exec(timeframe);
  if (monthlyMatch) {
    return timeframe; // Already in correct format
  }

  // Handle weekly date range format (e.g., "Nov. 18-24, 2024" or "Oct. 28 - Nov. 3, 2024")
  const weekRangeMatch = RegExp(
    /^([A-Za-z]+\. \d{1,2}(?:-\d{1,2})?(?:\s-\s[A-Za-z]+\. \d{1,2})?, \d{4})$/
  ).exec(timeframe);
  if (weekRangeMatch) {
    return timeframe; // Already in correct weekly format
  }

  // Handle legacy "Week of [Month] [Day], [Year]" format (keep as weekly for pet parent reports)
  const weekMatch = RegExp(/Week of ([A-Za-z]+) (\d{1,2}), (\d{4})/).exec(timeframe);
  if (weekMatch) {
    const [, monthStr, dayStr, yearStr] = weekMatch;
    return `Week of ${monthStr} ${dayStr}, ${yearStr}`;
  }

  return timeframe;
};

/**
 * Formats a weekly timeframe for compact display
 * Example: "Week of January 06, 2025" → "Week of Jan 6, 2025"
 */
export const formatWeeklyTimeframe = (timeframe: string): string => {
  if (timeframe.startsWith('Week of')) {
    // Extract the date part and format it more compactly
    const dateMatch = RegExp(/Week of ([A-Za-z]+ \d{1,2}, \d{4})/).exec(timeframe);
    if (dateMatch) {
      try {
        const datePart = dateMatch[1];
        const parsedDate = new Date(datePart);
        if (!isNaN(parsedDate.getTime())) {
          return `Week of ${format(parsedDate, 'MMM d, yyyy')}`;
        }
      } catch {
        // Fall back to original format if parsing fails
      }
    }
  }
  return timeframe;
};

/**
 * Formats a timeframe_start date to monthly display format
 * Example: "2025-10-01" → "October 2025"
 *
 * IMPORTANT: Uses utcCalendarDateToLocal to handle UTC timestamps correctly.
 * Without this, dates like "2025-12-01T00:00:00.000Z" would display as
 * "November 2025" in PST timezone instead of "December 2025".
 */
export const formatTimeframeStartToDisplay = (timeframeStart: string): string => {
  try {
    // Convert UTC calendar date to local Date with same calendar date
    const startDate = utcCalendarDateToLocal(timeframeStart);
    if (!startDate) {
      return timeframeStart;
    }
    return format(startDate, 'MMMM yyyy');
  } catch {
    return timeframeStart;
  }
};

/**
 * Formats a timeframe_start date to month display format
 * Example: "2025-10-01" → "October 2025"
 *
 * IMPORTANT: Uses utcCalendarDateToLocal to handle UTC timestamps correctly.
 * Without this, dates like "2025-12-01T00:00:00.000Z" would display as
 * "November 2025" in PST timezone instead of "December 2025".
 */
export const formatTimeframeToMonth = (timeframeStart: string): string => {
  try {
    // Convert UTC calendar date to local Date with same calendar date
    const date = utcCalendarDateToLocal(timeframeStart);
    if (!date) {
      return timeframeStart;
    }
    return format(date, 'MMMM yyyy');
  } catch {
    return timeframeStart;
  }
};

/**
 * Calculates the total from adoption counts
 */
export const calculateTotalAdoptions = (counts: {
  adultSeniorDogs: number;
  puppies: number;
  adultSeniorCats: number;
  kittens: number;
}): number => {
  return counts.adultSeniorDogs + counts.puppies + counts.adultSeniorCats + counts.kittens;
};
